import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  Grid,
  Divider,
  Paper,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import ArrowBack from "@mui/icons-material/ArrowBack";
import ErrorIcon from "@mui/icons-material/Error";
import api from "../../api/axios";
import LoadingScreen from "../../components/LoadingScreen";

/* ── Animation keyframes ── */
const filterAnimStyles = `
@keyframes cardFadeIn {
  from { opacity: 0; transform: scale(0.92); }
  to   { opacity: 1; transform: scale(1); }
}
`;

const FILTERS = [
  { key: "ALL", label: "All Orders", color: "#64748b" },
  { key: "ORDER_PLACED", label: "New Orders", color: "#1976d2" },
  { key: "VENDOR_ACCEPTED", label: "Accepted", color: "#16a34a" },
  { key: "LOOKING_FOR_AGENT", label: "Finding Agent", color: "#94a3b8" },
  { key: "AGENT_ACCEPTED", label: "Agent Accepted", color: "#06b6d4" },
  { key: "VENDOR_PREPARING", label: "Preparing", color: "#f59e0b" },
  { key: "VENDOR_READY", label: "Ready for Pickup", color: "#8b5cf6" },
  { key: "PICKED_UP", label: "Picked Up", color: "#0ea5e9" },
  { key: "IN_TRANSIT", label: "In Transit", color: "#f97316" },
  { key: "DELIVERED", label: "Delivered", color: "#22c55e" },
  { key: "CANCELLED", label: "Cancelled / Rejected", color: "#ef4444" },
];

const VendorOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [filterKey, setFilterKey] = useState("ALL");
  const [loading, setLoading] = useState(true);

  const vendorUser = JSON.parse(localStorage.getItem("user"));

  useEffect(() => {
    if (!vendorUser?._id) {
      navigate("/vendor/login", { replace: true });
      return;
    }
    fetchVendorOrders();
  }, []);

  const fetchVendorOrders = async () => {
    try {
      const { data } = await api.get("/vendor/orders", {
        params: { vendorId: vendorUser._id },
      });
      setOrders(data);
    } catch (err) {
      console.error(err.response?.data?.message || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ORDER_PLACED": return "info";
      case "SHIPPED": return "warning";
      case "DELIVERED": return "success";
      case "CANCELLED": return "error";
      default: return "default";
    }
  };

  const getStatusIcon = (status) => {
    if (status === "SHIPPED" || status === "DELIVERED") {
      return <LocalShippingIcon sx={{ fontSize: 16, mr: 0.5 }} />;
    }
    return null;
  };

  /* ── Filter logic ── */
  const passesFilter = (order) => {
    if (filterKey === "ALL") return true;

    const oStatus = order.status || "";

    switch (filterKey) {
      case "ORDER_PLACED":       return oStatus === "ORDER_PLACED";
      case "VENDOR_ACCEPTED":    return oStatus === "VENDOR_ACCEPTED";
      case "LOOKING_FOR_AGENT":  return oStatus === "LOOKING_FOR_AGENT";
      case "AGENT_ACCEPTED":     return oStatus === "AGENT_ACCEPTED";
      case "VENDOR_PREPARING":   return oStatus === "VENDOR_PREPARING";
      case "VENDOR_READY":       return oStatus === "VENDOR_READY";
      case "PICKED_UP":          return oStatus === "PICKED_UP";
      case "IN_TRANSIT":         return oStatus === "IN_TRANSIT";
      case "DELIVERED":          return oStatus === "DELIVERED";
      case "CANCELLED":          return oStatus === "CANCELLED";
      default: return true;
    }
  };

  const filteredOrders = orders.filter(passesFilter);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f0f2f5" }}>
      <style>{filterAnimStyles}</style>

      {/* ================= HEADER ================= */}
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #0062ff 100%)', px: { xs: 2, sm: 4 }, py: 2.5, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 0.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Button onClick={() => navigate("/vendor/dashboard")} sx={{ color: "rgba(255,255,255,0.7)", textTransform: "none", fontSize: "0.82rem", fontWeight: 500, mb: 0.5, p: 0, "&:hover": { color: "#fff", bgcolor: "transparent" }, display: "flex", alignItems: "center", gap: 0.5 }}>
              <ArrowBack sx={{ fontSize: 16 }} />
              Back to Dashboard
            </Button>
            <Typography variant="h5" fontWeight={700} sx={{ color: '#fff' }}>📋 Manage Orders</Typography>
          </Box>
        </Box>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>Manage and track all orders assigned to you.</Typography>
      </Box>

      <Box sx={{ px: { xs: 2, sm: 4 }, pb: 4 }}>
        {/* ================= Section Header ================= */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Box sx={{ width: 4, height: 22, borderRadius: 2, bgcolor: '#1976d2' }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1e293b' }}>
            Orders
          </Typography>
        </Box>

        {/* ================= Filter Buttons ================= */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mb: 3,
          }}
        >
          {FILTERS.map((f) => (
            <Button
              key={f.key}
              variant={filterKey === f.key ? "contained" : "outlined"}
              size="small"
              onClick={() => setFilterKey(f.key)}
              sx={{
                textTransform: 'none',
                borderRadius: '20px',
                fontWeight: 600,
                fontSize: '0.75rem',
                px: 1.75,
                py: 0.4,
                borderColor: filterKey === f.key ? f.color : '#e2e8f0',
                backgroundColor: filterKey === f.key ? f.color : 'transparent',
                color: filterKey === f.key ? '#fff' : '#64748b',
                '&:hover': {
                  backgroundColor: filterKey === f.key ? f.color : '#f1f5f9',
                  borderColor: f.color,
                  opacity: 0.9,
                },
                transition: 'all 0.2s ease',
              }}
            >
              {f.label}
            </Button>
          ))}
        </Box>

        {/* Loading State */}
        {loading && <LoadingScreen message="Loading your orders..." />}

        {/* No Orders */}
        {!loading && orders.length === 0 && (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <ErrorIcon sx={{ fontSize: 48, color: "textSecondary", mb: 2 }} />
            <Typography variant="h6" color="textSecondary">No orders yet</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>New orders will appear here</Typography>
          </Paper>
        )}

        {/* No results for filter */}
        {!loading && orders.length > 0 && filteredOrders.length === 0 && (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <ErrorIcon sx={{ fontSize: 48, color: "textSecondary", mb: 2 }} />
            <Typography variant="h6" color="textSecondary">No matching orders</Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>Try a different filter</Typography>
          </Paper>
        )}

        {/* Orders List - 3 columns on desktop */}
        <Box
          key={filterKey}
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
            },
            alignItems: 'stretch',
          }}
        >
          {filteredOrders.map((order) => (
            <Card
              key={order._id}
              sx={{
                borderRadius: '12px',
                border: '1.5px solid #f1f5f9',
                boxShadow: '0 2px 10px rgba(15,23,42,0.06)',
                transition: 'transform 0.18s ease, boxShadow 0.18s ease',
                animation: 'cardFadeIn 0.35s ease',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(15,23,42,0.10)' },
              }}
            >
              {/* Order Header */}
              <Box
                sx={{
                  p: 2,
                  borderBottom: "1px solid #f1f5f9",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography variant="caption" color="textSecondary" display="block">Order ID</Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ fontFamily: "monospace", color: "#1976d2" }}>
                    {order._id.slice(-12).toUpperCase()}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="caption" color="textSecondary" display="block">Order Date</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {new Date(order.createdAt).toLocaleDateString("en-IN", {
                      year: "numeric", month: "short", day: "numeric",
                    })}
                  </Typography>
                </Box>
              </Box>

              <CardContent sx={{ p: 2 }}>
                {/* Items Section */}
                <Stack spacing={2}>
                  {order.items?.map((item, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        gap: 2,
                        pb: 2,
                        borderBottom: idx < order.items.length - 1 ? "1px solid #e0e0e0" : "none",
                      }}
                    >
                      {/* Product Image */}
                      <Box
                        sx={{
                          flexShrink: 0,
                          width: 100,
                          height: 100,
                          backgroundColor: "#f5f5f5",
                          borderRadius: 1,
                          overflow: "hidden",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        {item.imageUrl ? (
                          <img src={item.imageUrl} alt={item.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        ) : (
                          <Typography variant="caption" color="textSecondary">No Image</Typography>
                        )}
                      </Box>

                      {/* Product Details */}
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          sx={{ mb: 1, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                        >
                          {item.title}
                        </Typography>

                        <Grid container spacing={2} sx={{ mb: 1 }}>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="textSecondary" display="block">Price per unit</Typography>
                            <Typography variant="body2" fontWeight={600} sx={{ color: "#b12704" }}>
                              ₹{Number(item.price).toLocaleString("en-IN")}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="caption" color="textSecondary" display="block">Quantity</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {item.qty} {item.qty > 1 ? "items" : "item"}
                            </Typography>
                          </Grid>
                        </Grid>

                        <Typography variant="caption" sx={{ color: "#b12704", fontWeight: 600, display: "block" }}>
                          Subtotal: ₹{Number(item.price * item.qty).toLocaleString("en-IN")}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>

                <Divider sx={{ my: 2 }} />

                {/* Order Summary */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">Delivery Address</Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {order.address?.addressLine1}, {order.address?.addressLine2}
                      </Typography>
                      <Typography variant="body2">
                        {order.address?.city}, {order.address?.state} {order.address?.pincode}
                      </Typography>
                      <Typography variant="body2">{order.address?.country}</Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6} sx={{ textAlign: { xs: "left", sm: "right" } }}>
                    <Box>
                      <Typography variant="caption" color="textSecondary" display="block">Order Total</Typography>
                      <Typography variant="h6" fontWeight={700} sx={{ color: "#b12704", mt: 0.5 }}>
                        ₹{Number(order.total).toLocaleString("en-IN")}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                        Payment: {order.paymentMethod?.toUpperCase()}
                      </Typography>
                      <Chip
                        icon={getStatusIcon(order.status)}
                        label={order.tracking?.at(-1)?.label || "Processing"}
                        color={getStatusColor(order.status)}
                        size="small"
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Action Buttons */}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ justifyContent: "flex-end" }}>
                  {order.status === "ORDER_PLACED" && (
                    <Button
                      variant="contained"
                      sx={{ backgroundColor: "#ff9900", "&:hover": { backgroundColor: "#e68a00" } }}
                      onClick={() => navigate(`/vendor/orders/${order._id}`)}
                    >
                      Review & Accept
                    </Button>
                  )}
                  <Button variant="outlined" onClick={() => navigate(`/vendor/orders/${order._id}`)}>
                    View Details
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default VendorOrders;